import { Alert, Box, CircularProgress } from '@mui/material';
import React, { useEffect } from 'react';
import CorrelationSection from '../team-report/CorrelationSection';
import DomainRankingsSection from '../team-report/DomainRankingsSection';
import TierSection from '../team-report/TierSection';
import VarianceSection from '../team-report/VarianceSection';
import {
    CorrelationSection as CorrelationData,
    DomainRankingRow,
    ReportSection,
    TierSection as TierData,
    VarianceRow,
} from '../team-report/types';
import PreAssessmentCompletionSection from '../session-engagement/PreAssessmentCompletionSection';
import SessionsCompletedThresholdSection from '../session-engagement/SessionsCompletedThresholdSection';
import {
    PreAssessmentCompletionSection as PreAssessmentData,
    SessionsCompletedThresholdRow,
} from '../session-engagement/types';
import { ReportVizId, REPORT_VIZ_BY_ID } from './catalog';
import GrowthReportSection from './GrowthReportSection';
import SurveySubmissionsSection from './SurveySubmissionsSection';
import { useReportSection } from './useReportSection';

type Props = {
    vizId: ReportVizId;
    onData?: (vizId: ReportVizId, data: unknown | null) => void;
};

function SectionLoading() {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
        </Box>
    );
}

function SectionError({ message }: { message: string }) {
    return (
        <Box sx={{ mb: 3 }}>
            <Alert severity="error">{message}</Alert>
        </Box>
    );
}

const ReportSectionHost: React.FC<Props> = ({ vizId, onData }) => {
    if (vizId === 'growth') {
        return <GrowthReportSection />;
    }
    if (vizId === 'survey-submissions') {
        return <SurveySubmissionsSection />;
    }

    const endpoint = REPORT_VIZ_BY_ID[vizId].endpoint;

    if (vizId === 'domain-rankings') {
        return <DomainRankingsHost endpoint={endpoint} onData={onData} />;
    }
    if (vizId === 'variance') {
        return <VarianceHost endpoint={endpoint} onData={onData} />;
    }
    if (vizId === 'tier-segmentation') {
        return <TierHost endpoint={endpoint} onData={onData} />;
    }
    if (vizId === 'domain-correlation') {
        return <CorrelationHost endpoint={endpoint} onData={onData} />;
    }
    if (vizId === 'pre-assessment-completion') {
        return <PreAssessmentHost endpoint={endpoint} onData={onData} />;
    }
    if (vizId === 'sessions-completed-threshold') {
        return <SessionsThresholdHost endpoint={endpoint} onData={onData} />;
    }

    return null;
};

function useNotifyData(vizId: ReportVizId, data: unknown | null, loading: boolean, onData?: Props['onData']) {
    useEffect(() => {
        if (!onData || loading) return;
        onData(vizId, data);
        return () => onData(vizId, null);
    }, [vizId, data, loading, onData]);
}

function DomainRankingsHost({ endpoint, onData }: { endpoint: string; onData?: Props['onData'] }) {
    const { data, loading, error } = useReportSection<ReportSection<DomainRankingRow> & { header?: unknown }>(endpoint);
    useNotifyData('domain-rankings', data, loading, onData);
    if (loading) return <SectionLoading />;
    if (error || !data) return <SectionError message={error || 'No data'} />;
    return <DomainRankingsSection data={{ rows: data.rows, key_takeaway: data.key_takeaway }} />;
}

function VarianceHost({ endpoint, onData }: { endpoint: string; onData?: Props['onData'] }) {
    const { data, loading, error } = useReportSection<ReportSection<VarianceRow>>(endpoint);
    useNotifyData('variance', data, loading, onData);
    if (loading) return <SectionLoading />;
    if (error || !data) return <SectionError message={error || 'No data'} />;
    return <VarianceSection data={data} />;
}

function TierHost({ endpoint, onData }: { endpoint: string; onData?: Props['onData'] }) {
    const { data, loading, error } = useReportSection<TierData>(endpoint);
    useNotifyData('tier-segmentation', data, loading, onData);
    if (loading) return <SectionLoading />;
    if (error || !data) return <SectionError message={error || 'No data'} />;
    return <TierSection data={data} />;
}

function CorrelationHost({ endpoint, onData }: { endpoint: string; onData?: Props['onData'] }) {
    const { data, loading, error } = useReportSection<CorrelationData>(endpoint);
    useNotifyData('domain-correlation', data, loading, onData);
    if (loading) return <SectionLoading />;
    if (error || !data) return <SectionError message={error || 'No data'} />;
    return <CorrelationSection data={data} />;
}

type PreAssessmentResponse = PreAssessmentData & {
    total_organization_athletes: number;
};

function PreAssessmentHost({ endpoint, onData }: { endpoint: string; onData?: Props['onData'] }) {
    const { data, loading, error } = useReportSection<PreAssessmentResponse>(endpoint);
    useNotifyData('pre-assessment-completion', data, loading, onData);
    if (loading) return <SectionLoading />;
    if (error || !data) return <SectionError message={error || 'No data'} />;
    return (
        <PreAssessmentCompletionSection
            data={{
                completed_count: data.completed_count,
                percent: data.percent,
                key_takeaway: data.key_takeaway,
            }}
            totalOrganizationAthletes={data.total_organization_athletes}
        />
    );
}

type SessionsThresholdResponse = {
    total_organization_athletes: number;
    rows: SessionsCompletedThresholdRow[];
    key_takeaway: string;
};

function SessionsThresholdHost({ endpoint, onData }: { endpoint: string; onData?: Props['onData'] }) {
    const { data, loading, error } = useReportSection<SessionsThresholdResponse>(endpoint);
    useNotifyData('sessions-completed-threshold', data, loading, onData);
    if (loading) return <SectionLoading />;
    if (error || !data) return <SectionError message={error || 'No data'} />;
    return (
        <SessionsCompletedThresholdSection
            data={{ rows: data.rows, key_takeaway: data.key_takeaway }}
            totalOrganizationAthletes={data.total_organization_athletes}
        />
    );
}

export default ReportSectionHost;
